import { useState } from "react";
import { subscriptionService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

export const useWorkOrderTemplateViewModel = ({ workOrderTemplateId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workOrderTemplate, setWorkOrderTemplate] = useState({});
  const [trades, setTrades] = useState([]);
  const stateManager = useComponentState();

  const getWorkOrderTemplateDetails = () => {
    stateManager.run(async () => {
      const response = await taskService.workOrderTemplates.getById({
        id: workOrderTemplateId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setWorkOrderTemplate(response.data);
      }
    });
  };

  const getTrades = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.trades.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        const updatedTrades = response.data.map(({ name, id }) => ({
          label: name,
          value: id,
        }));

        setTrades(updatedTrades);
      }
    });
  };

  const getAllAssignmentTemplates = () => {
    return stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getAll({
        sort: "name asc",
      });

      if (response.status === HTTP_STATUSES.OK) {
        const existingTemplateIds = new Set(
          workOrderTemplate?.assignmentTemplates?.map(({ id }) => id)
        );

        return response?.data?.filter(({ id }) => !existingTemplateIds.has(id));
      }

      return [];
    });
  };

  const handleAddAssignmentTemplate = ({ assignmentTemplateId, ...data }) => {
    stateManager.run(async () => {
      const { status } =
        await taskService.workOrderTemplateAssignmentTemplateMaps.create({
          data: {
            orderTemplateId: workOrderTemplateId,
            assignmentTemplateId,
            ...data,
          },
          toastMessageConfig: {
            success: {
              text: `${data.name} was added.`,
            },
          },
        });

      if (status === HTTP_STATUSES.OK) {
        closeModal();
        getWorkOrderTemplateDetails();
      }
    });
  };

  const onDetailsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = workOrderTemplate;

      const { status } = await taskService.workOrderTemplates.update({
        id: workOrderTemplateId,
        data: { ...data, modifiedOn },
        toastMessageConfig: {
          success: {
            text: `${name}'s details were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getWorkOrderTemplateDetails();
      }
    });
  };

  const onInputOptionsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = workOrderTemplate;
      const { status } = await taskService.workOrderTemplates.updateConfigById({
        id: workOrderTemplateId,
        data: { fields: data },
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${name}'s input options were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getWorkOrderTemplateDetails();
      }
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return {
    workOrderTemplate,
    loading: stateManager.isBusy(),
    isModalOpen,
    getAllAssignmentTemplates,
    handleAddAssignmentTemplate,
    closeModal,
    openModal,
    getWorkOrderTemplateDetails,
    onDetailsUpdate,
    onInputOptionsUpdate,
    getTrades,
    trades,
  };
};
